import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { apiClient } from "@lib/apiClient";

/**
 * Native iOS push registration. In the Capacitor app (where service workers —
 * and therefore Web Push — don't run), register with APNs and hand the device
 * token to the Worker, which delivers alerts via Apple's push service.
 * No-op on the web.
 */
export function useApnsRegistration(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !Capacitor.isNativePlatform()) return;
    let cancelled = false;

    void (async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const perm = await PushNotifications.requestPermissions();
        if (cancelled || perm.receive !== "granted") return;

        await PushNotifications.addListener("registration", (token) => {
          void apiClient.post("/push/apns", { token: token.value }).catch(() => {});
        });
        await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
          const url = (action.notification.data as { url?: string } | undefined)?.url;
          if (url && url.startsWith("/")) window.location.assign(url);
        });
        await PushNotifications.register();
      } catch {
        // Plugin unavailable (web build) or registration failed — non-fatal.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
